import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, Settings, Trash2, Eye, RefreshCw, Send, Users, BarChart3, Check, Volume2, VolumeX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNotifications } from '../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { firebaseService } from '../services/firebaseService';
import { adminNotificationService } from '../services/notificationService';
import { useAuth } from '../hooks/use-auth';
import { useToast } from '../hooks/use-toast';

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    settings, 
    soundSettings,
    markAsRead, 
    markAllAsRead, 
    deleteNotification, 
    refreshNotifications,
    updateSettings,
    updateSoundSettings,
    testSound
  } = useNotifications();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Admin states
  const [activeTab, setActiveTab] = useState('notifications');
  const [sendFormData, setSendFormData] = useState({
    users: [] as string[],
    title: '',
    body: '',
    type: 'general',
    channels: ['push'] as ('push' | 'email' | 'sms')[],
    data: {}
  });

  // User selection states
  const [userSearch, setUserSearch] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Array<{id: string, name: string, email: string}>>([]);

  // Check if user is admin
  const isAdmin = user?.roleId === 2;

  // Users query for notification targeting
  const { data: availableUsers = [], isLoading: isUsersLoading } = useQuery({
    queryKey: ['notification-users', userSearch],
    queryFn: () => adminNotificationService.getUsers(userSearch),
    enabled: isAdmin && userSearch.length >= 2,
    staleTime: 30000,
  });

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.body.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || notification.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'read' && notification.is_read) ||
                         (statusFilter === 'unread' && !notification.is_read);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Helper functions
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'booking': return '📅';
      case 'promotion': return '🎉';
      case 'review': return '⭐';
      case 'system_update': return '🔧';
      case 'payment': return '💳';
      default: return '🔔';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'booking': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'promotion': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'review': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'system_update': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'payment': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const handleSettingsUpdate = (newSettings: any) => {
    if (settings) {
      updateSettings({
        ...settings,
        ...newSettings
      });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
  };

  const requestNotificationPermission = async () => {
    try {
      if (firebaseService.isSupported()) {
        await firebaseService.initialize();
        toast({
          title: t("notificationPermissionGranted"),
          description: t("youWillReceiveNotifications"),
        });
      } else {
        toast({
          title: t("notificationNotSupported"),
          description: t("yourBrowserDoesNotSupportNotifications"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast({
        title: t("notificationPermissionError"),
        description: t("failedToEnableNotifications"),
        variant: "destructive",
      });
    }
  };

  // User selection helper functions
  const addUser = (user: {id: string, name: string, email: string}) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      const newSelectedUsers = [...selectedUsers, user];
      setSelectedUsers(newSelectedUsers);
      setSendFormData(prev => ({ 
        ...prev, 
        users: newSelectedUsers.map(u => u.id) 
      }));
    }
    setUserSearch('');
    setIsUserDropdownOpen(false);
  };

  const removeUser = (userId: string) => {
    const newSelectedUsers = selectedUsers.filter(u => u.id !== userId);
    setSelectedUsers(newSelectedUsers);
    setSendFormData(prev => ({ 
      ...prev, 
      users: newSelectedUsers.map(u => u.id) 
    }));
  };

  const clearAllUsers = () => {
    setSelectedUsers([]);
    setSendFormData(prev => ({ ...prev, users: [] }));
  };

  // Send notification mutation
  const sendNotificationMutation = useMutation({
    mutationFn: (data: typeof sendFormData) => adminNotificationService.sendNotification({
      ...data,
      data: {
        type: data.type,
        action: data.type === 'promotion' ? 'open_bookings' : undefined
      }
    }),
    onSuccess: () => {
      toast({
        title: t('notificationSent'),
        description: t('notificationSentSuccessfully'),
      });
      // Reset form
      setSendFormData({
        users: [],
        title: '',
        body: '',
        type: 'general',
        channels: ['push'] as ('push' | 'email' | 'sms')[],
        data: {}
      });
      setSelectedUsers([]);
    },
    onError: (error: any) => {
      toast({
        title: t('error'),
        description: error.message || t('failedToSendNotification'),
        variant: 'destructive',
      });
    },
  });

  const handleSendNotification = () => {
    if (!sendFormData.title || !sendFormData.body || sendFormData.users.length === 0) {
      toast({
        title: t('validationError'),
        description: t('pleaseCompleteAllRequiredFields'),
        variant: 'destructive',
      });
      return;
    }

    sendNotificationMutation.mutate(sendFormData);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('notifications')}</h1>
          <p className="text-muted-foreground">
            {isAdmin ? t('manageAndSendNotifications') : t('manageYourNotifications')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={refreshNotifications}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {t('refresh')}
          </Button>
          
          <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                {t('settings')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('notificationSettings')}</DialogTitle>
                <DialogDescription>
                  {t('configureYourNotificationPreferences')}
                </DialogDescription>
              </DialogHeader>
              
              {settings && (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="push-enabled">{t('pushNotifications')}</Label>
                      <Switch
                        id="push-enabled"
                        checked={settings.push_enabled}
                        onCheckedChange={(checked) => 
                          handleSettingsUpdate({ push_enabled: checked })
                        }
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-enabled">{t('emailNotifications')}</Label>
                      <Switch
                        id="email-enabled"
                        checked={settings.email_enabled}
                        onCheckedChange={(checked) => 
                          handleSettingsUpdate({ email_enabled: checked })
                        }
                      />
                    </div>
                  </div>

                  <Separator />
                  
                  {/* Sound Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      {soundSettings.enabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                      {t('soundSettings')}
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sound-enabled">{t('enableSounds')}</Label>
                        <Switch
                          id="sound-enabled"
                          checked={soundSettings.enabled}
                          onCheckedChange={(checked) => 
                            updateSoundSettings({ enabled: checked })
                          }
                        />
                      </div>
                      
                      {soundSettings.enabled && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="sound-volume">{t('volume')}</Label>
                            <div className="flex items-center space-x-3">
                              <VolumeX className="h-4 w-4 text-muted-foreground" />
                              <Slider
                                id="sound-volume"
                                min={0}
                                max={1}
                                step={0.1}
                                value={[soundSettings.volume]}
                                onValueChange={(value) => 
                                  updateSoundSettings({ volume: value[0] })
                                }
                                className="flex-1"
                              />
                              <Volume2 className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {Math.round(soundSettings.volume * 100)}%
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="sound-type">{t('soundType')}</Label>
                            <Select
                              value={soundSettings.soundType}
                              onValueChange={(value: any) => 
                                updateSoundSettings({ soundType: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">{t('default')}</SelectItem>
                                <SelectItem value="chime">{t('chime')}</SelectItem>
                                <SelectItem value="bell">{t('bell')}</SelectItem>
                                <SelectItem value="pop">{t('pop')}</SelectItem>
                                <SelectItem value="ding">{t('ding')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => testSound(soundSettings.soundType)}
                            >
                              <Volume2 className="h-4 w-4 mr-2" />
                              {t('testSound')}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                console.log('🔊 Manual sound test triggered');
                                testSound('default');
                              }}
                            >
                              Debug Sound
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {firebaseService.getPermissionStatus() !== 'granted' && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          {t('enableBrowserNotifications')}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={requestNotificationPermission}
                          className="w-full"
                        >
                          {t('enableNotifications')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={() => setIsSettingsOpen(false)}>
                  {t('close')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Main Content */}
      {isAdmin ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="notifications">{t('myNotifications')}</TabsTrigger>
            <TabsTrigger value="send">{t('sendNotification')}</TabsTrigger>
            <TabsTrigger value="statistics">{t('statistics')}</TabsTrigger>
          </TabsList>

          <TabsContent value="notifications" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('totalNotifications')}</CardTitle>
                  <Bell className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{notifications.length}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('unreadNotifications')}</CardTitle>
                  <BellRing className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{unreadCount}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('readNotifications')}</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{notifications.length - unreadCount}</div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex flex-col sm:flex-row gap-4 flex-1">
                    <Input
                      placeholder={t('searchNotifications')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="max-w-sm"
                    />
                    
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder={t('allTypes')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allTypes')}</SelectItem>
                        <SelectItem value="booking">{t('booking')}</SelectItem>
                        <SelectItem value="promotion">{t('promotion')}</SelectItem>
                        <SelectItem value="review">{t('review')}</SelectItem>
                        <SelectItem value="system_update">{t('systemUpdate')}</SelectItem>
                        <SelectItem value="payment">{t('payment')}</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder={t('allStatuses')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allStatuses')}</SelectItem>
                        <SelectItem value="read">{t('read')}</SelectItem>
                        <SelectItem value="unread">{t('unread')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex gap-2">
                    {(searchTerm || typeFilter !== 'all' || statusFilter !== 'all') && (
                      <Button variant="outline" onClick={clearFilters}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t('clearFilters')}
                      </Button>
                    )}
                    
                    {unreadCount > 0 && (
                      <Button onClick={markAllAsRead}>
                        <Eye className="h-4 w-4 mr-2" />
                        {t('markAllAsRead')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Notifications List */}
            <div className="space-y-4">
              {isLoading ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center text-muted-foreground">
                      {t('loading')}...
                    </div>
                  </CardContent>
                </Card>
              ) : filteredNotifications.length === 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center">
                      <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">
                        {notifications.length === 0 ? t('noNotifications') : t('noNotificationsMatch')}
                      </h3>
                      <p className="text-muted-foreground">
                        {notifications.length === 0 
                          ? t('youWillSeeNotificationsHere') 
                          : t('tryAdjustingYourFilters')
                        }
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredNotifications.map((notification) => (
                  <Card 
                    key={notification.id} 
                    className={`transition-colors ${
                      !notification.is_read ? 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800' : ''
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="text-2xl">
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <h4 className={`font-medium ${
                                !notification.is_read ? 'font-semibold' : ''
                              }`}>
                                {notification.title}
                              </h4>
                              {!notification.is_read && (
                                <div className="h-2 w-2 bg-blue-600 rounded-full" />
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Badge className={getNotificationColor(notification.type)}>
                                {t(notification.type)}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          
                          <p className="text-muted-foreground">
                            {notification.body}
                          </p>
                          
                          <div className="flex items-center space-x-2 pt-2">
                            {!notification.is_read && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => markAsRead(notification.id)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                {t('markAsRead')}
                              </Button>
                            )}
                            
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  {t('delete')}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{t('deleteNotification')}</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t('deleteNotificationConfirmation')}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteNotification(notification.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    {t('delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="send" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('sendNotification')}</CardTitle>
                <CardDescription>
                  {t('sendNotificationToUsers')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* User Selection */}
                <div className="space-y-2">
                  <Label>{t('selectUsers')} *</Label>
                  <div className="space-y-3">
                    {/* Selected Users Display */}
                    {selectedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/50">
                        {selectedUsers.map((user) => (
                          <Badge key={user.id} variant="secondary" className="flex items-center gap-1">
                            <span>{user.name}</span>
                            <button
                              type="button"
                              onClick={() => removeUser(user.id)}
                              className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearAllUsers}
                          className="h-6 px-2 text-xs"
                        >
                          {t('clearAll')}
                        </Button>
                      </div>
                    )}
                    
                    {/* User Search Input */}
                    <div className="space-y-2">
                      <Input
                        placeholder={t('searchUsers')}
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="w-full"
                      />
                      
                      {/* Search Results */}
                      {userSearch.length >= 2 && (
                        <div className="border rounded-md max-h-48 overflow-y-auto">
                          {isUsersLoading ? (
                            <div className="p-3 text-center text-muted-foreground">
                              {t('loading')}...
                            </div>
                          ) : availableUsers.length === 0 ? (
                            <div className="p-3 text-center text-muted-foreground">
                              {t('noUsersFound')}
                            </div>
                          ) : (
                            availableUsers.map((user: any) => (
                              <div
                                key={user.id}
                                className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                                onClick={() => addUser({
                                  id: user.id,
                                  name: user.name || user.email,
                                  email: user.email
                                })}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{user.name || user.email}</span>
                                  {user.name && (
                                    <span className="text-xs text-muted-foreground">{user.email}</span>
                                  )}
                                </div>
                                {selectedUsers.find(u => u.id === user.id) && (
                                  <Check className="h-4 w-4 text-green-600" />
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      
                      {userSearch.length > 0 && userSearch.length < 2 && (
                        <p className="text-xs text-muted-foreground">
                          {t('typeAtLeast2Characters')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">{t('title')} *</Label>
                  <Input
                    id="title"
                    placeholder={t('enterNotificationTitle')}
                    value={sendFormData.title}
                    onChange={(e) => setSendFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="body">{t('message')} *</Label>
                  <Textarea
                    id="body"
                    placeholder={t('enterNotificationMessage')}
                    value={sendFormData.body}
                    onChange={(e) => setSendFormData(prev => ({ ...prev, body: e.target.value }))}
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="type">{t('notificationType')}</Label>
                  <Select
                    value={sendFormData.type}
                    onValueChange={(value) => setSendFormData(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">{t('general')}</SelectItem>
                      <SelectItem value="booking">{t('booking')}</SelectItem>
                      <SelectItem value="promotion">{t('promotion')}</SelectItem>
                      <SelectItem value="system_update">{t('systemUpdate')}</SelectItem>
                      <SelectItem value="payment">{t('payment')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Channels Selection */}
                <div className="space-y-2">
                  <Label>{t('notificationChannels')}</Label>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="push-channel"
                        checked={sendFormData.channels.includes('push')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSendFormData(prev => ({ 
                              ...prev, 
                              channels: [...prev.channels.filter(c => c !== 'push'), 'push'] 
                            }));
                          } else {
                            setSendFormData(prev => ({ 
                              ...prev, 
                              channels: prev.channels.filter(c => c !== 'push') 
                            }));
                          }
                        }}
                      />
                      <Label htmlFor="push-channel">{t('pushNotification')}</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="email-channel"
                        checked={sendFormData.channels.includes('email')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSendFormData(prev => ({ 
                              ...prev, 
                              channels: [...prev.channels.filter(c => c !== 'email'), 'email'] 
                            }));
                          } else {
                            setSendFormData(prev => ({ 
                              ...prev, 
                              channels: prev.channels.filter(c => c !== 'email') 
                            }));
                          }
                        }}
                      />
                      <Label htmlFor="email-channel">{t('emailNotification')}</Label>
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full" 
                  disabled={!sendFormData.title || !sendFormData.body || sendFormData.users.length === 0 || sendNotificationMutation.isPending}
                  onClick={handleSendNotification}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendNotificationMutation.isPending ? t('sending') : t('sendNotification')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="statistics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('totalSent')}</CardTitle>
                  <Send className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">0</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('totalDelivered')}</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">0</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('totalRead')}</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">0</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('totalClicked')}</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">0</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        // Regular user view - same as notifications tab above
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('totalNotifications')}</CardTitle>
                <Bell className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{notifications.length}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('unreadNotifications')}</CardTitle>
                <BellRing className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{unreadCount}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('readNotifications')}</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{notifications.length - unreadCount}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Notifications List - same as above */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  <Input
                    placeholder={t('searchNotifications')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm"
                  />
                  
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder={t('allTypes')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allTypes')}</SelectItem>
                      <SelectItem value="booking">{t('booking')}</SelectItem>
                      <SelectItem value="promotion">{t('promotion')}</SelectItem>
                      <SelectItem value="review">{t('review')}</SelectItem>
                      <SelectItem value="system_update">{t('systemUpdate')}</SelectItem>
                      <SelectItem value="payment">{t('payment')}</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder={t('allStatuses')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allStatuses')}</SelectItem>
                      <SelectItem value="read">{t('read')}</SelectItem>
                      <SelectItem value="unread">{t('unread')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2">
                  {(searchTerm || typeFilter !== 'all' || statusFilter !== 'all') && (
                    <Button variant="outline" onClick={clearFilters}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('clearFilters')}
                    </Button>
                  )}
                  
                  {unreadCount > 0 && (
                    <Button onClick={markAllAsRead}>
                      <Eye className="h-4 w-4 mr-2" />
                      {t('markAllAsRead')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Notifications List */}
          <div className="space-y-4">
            {isLoading ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center text-muted-foreground">
                    {t('loading')}...
                  </div>
                </CardContent>
              </Card>
            ) : filteredNotifications.length === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center">
                    <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold mb-2">
                      {notifications.length === 0 ? t('noNotifications') : t('noNotificationsMatch')}
                    </h3>
                    <p className="text-muted-foreground">
                      {notifications.length === 0 
                        ? t('youWillSeeNotificationsHere') 
                        : t('tryAdjustingYourFilters')
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              filteredNotifications.map((notification) => (
                <Card 
                  key={notification.id} 
                  className={`transition-colors ${
                    !notification.is_read ? 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800' : ''
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="text-2xl">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h4 className={`font-medium ${
                              !notification.is_read ? 'font-semibold' : ''
                            }`}>
                              {notification.title}
                            </h4>
                            {!notification.is_read && (
                              <div className="h-2 w-2 bg-blue-600 rounded-full" />
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Badge className={getNotificationColor(notification.type)}>
                              {t(notification.type)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-muted-foreground">
                          {notification.body}
                        </p>
                        
                        <div className="flex items-center space-x-2 pt-2">
                          {!notification.is_read && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              {t('markAsRead')}
                            </Button>
                          )}
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="h-3 w-3 mr-1" />
                                {t('delete')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('deleteNotification')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('deleteNotificationConfirmation')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteNotification(notification.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  {t('delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
